import { db } from "../firebase";
import { collection, query, where, getDocs, limit } from "firebase/firestore";

const _empCommCache = new Map(); // in-memory cache for the session

function normalizeRules(raw) {
  if (!raw) return [];
  const toArr = (arr) => (Array.isArray(arr) ? arr : []);
  // if raw is an array directly
  if (Array.isArray(raw)) {
    return raw.map(r => normalizeSingleRule(r));
  }
  // if raw is object containing items/commissions/etc
  if (typeof raw === "object") {
    const cand = raw.items || raw.commissions || raw.data || raw.rules || raw.itemsList || [];
    return toArr(cand).map(r => normalizeSingleRule(r));
  }
  return [];
}

function normalizeSingleRule(r) {
  const code = (r.code || r.itemCode || r.item || r.sku || "").toString().trim();
  const brand = (r.brand || r.brandName || r.manufacturer || "").toString().trim();
  const ratePercent = (r.ratePercent !== undefined) ? r.ratePercent : ((r.rate !== undefined) ? r.rate : ((r.percent !== undefined) ? r.percent : 0));
  const amount = (r.amount !== undefined) ? r.amount : ((r.fixedAmount !== undefined) ? r.fixedAmount : ((r.value !== undefined) ? r.value : 0));
  return {
    code: String(code),
    codeLower: String(code).toLowerCase(),
    brand: String(brand),
    brandLower: String(brand).toLowerCase(),
    ratePercent: Number(ratePercent || 0),
    amount: Number(amount || 0)
  };
}

async function fetchRulesForEmployee(employeeId) {
  if (!employeeId) return [];
  if (_empCommCache.has(employeeId)) return _empCommCache.get(employeeId);

  try {
    const q = query(
      collection(db, "employeeCommissions"),
      where("partyId", "==", employeeId),
      limit(1)
    );
    const snap = await getDocs(q);
    if (!snap.empty) {
      const docData = snap.docs[0].data() || {};
      const rulesRaw = docData.items || docData.commissions || docData || [];
      const rules = normalizeRules(rulesRaw);
      _empCommCache.set(employeeId, rules);
      console.debug("[comm-helper] fetched rules for emp", employeeId, rules);
      return rules;
    } else {
      console.debug("[comm-helper] no commission doc for emp", employeeId);
    }
  } catch (err) {
    console.warn("[comm-helper] fetchRulesForEmployee failed:", err);
  }
  _empCommCache.set(employeeId, []);
  return [];
}

export default async function computeCommissionsForSave(items = [], employeeId = null) {
  try {
    const rows = Array.isArray(items) ? items.map(it => ({ ...it })) : [];
    const rules = await fetchRulesForEmployee(employeeId);

    console.debug("[comm-helper] computeCommissionsForSave called", { itemsCount: rows.length, employeeId, rulesCount: (rules || []).length });

    // choose rule: prefer exact code, then longest startsWith code, then brand match, then global (empty code), then first
    const findRuleForItem = (itm) => {
      if (!rules || !rules.length) return { rule: null, reason: "no_rules" };

      const codeRaw = (itm.code || itm.itemCode || itm.sku || "").toString().trim();
      const code = codeRaw;
      const codeLower = codeRaw.toLowerCase();
      const brandRaw = (itm.brand || itm.brandName || itm.desc || "").toString().trim();
      const brand = brandRaw;
      const brandLower = brandRaw.toLowerCase();

      // exact code match (case-insensitive)
      if (code) {
        const exact = rules.find(r => r.code && r.codeLower === codeLower);
        if (exact) return { rule: exact, reason: "exact_code" };
      }

      // startsWith: pick the rule with the longest matching prefix (most specific)
      if (code) {
        let best = null;
        let bestLen = -1;
        for (const r of rules) {
          if (!r.code) continue;
          const rc = r.codeLower;
          if (rc && codeLower.startsWith(rc) && rc.length > bestLen) {
            best = r; bestLen = rc.length;
          }
        }
        if (best) return { rule: best, reason: "startsWith_code_longest" };
      }

      // brand match (case-insensitive, substring)
      if (brandLower) {
        const bm = rules.find(r => r.brand && r.brandLower && brandLower.indexOf(r.brandLower) !== -1);
        if (bm) return { rule: bm, reason: "brand" };
      }

      // global/default rule (empty code) — treat as last resort before absolute fallback
      const global = rules.find(r => !r.code || String(r.code).trim() === "");
      if (global) return { rule: global, reason: "global" };

      // fallback to first rule
      return { rule: rules[0], reason: "fallback_first" };
    };

    let total = 0;
    const out = rows.map((r, idx) => {
      const qty = Number(r.qty ?? r.quantity ?? 0) || 0;
      const rate = Number(r.rate ?? r.unitPrice ?? r.price ?? 0) || 0;
      const match = findRuleForItem(r);
      const chosen = match ? match.rule : null;
      const reason = match ? match.reason : "no_rule";

      let commissionAmount = 0;
      if (chosen) {
        // prefer fixed amount if present and >0
        if (Number(chosen.amount || 0) > 0) {
          commissionAmount = Number(chosen.amount || 0);
        } else if (Number(chosen.ratePercent || 0) > 0) {
          commissionAmount = (qty * rate) * (Number(chosen.ratePercent || 0) / 100);
        } else {
          commissionAmount = 0;
        }
      }

      commissionAmount = Number(commissionAmount || 0);
      total += commissionAmount;

      // lightweight log per item (can be noisy; remove in prod if needed)
      console.debug(`[comm-helper] item[${idx}] code='${r.code||""}' qty=${qty} rate=${rate} -> matched=${reason}`, { chosenRule: chosen ? { code: chosen.code, brand: chosen.brand, ratePercent: chosen.ratePercent, amount: chosen.amount } : null, commissionAmount });

      // ensure we return normalized commissionAmount field
      return { ...r, commissionAmount };
    });

    console.debug("[comm-helper] computed commissions total:", total);
    return { items: out, total: Number(total || 0) };
  } catch (err) {
    console.error("[comm-helper] computeCommissionsForSave error:", err);
    const safeItems = (Array.isArray(items) ? items : []).map(it => ({
      ...it,
      commissionAmount: Number(it.commissionAmount || 0) || 0
    }));
    const total = safeItems.reduce((s, it) => s + (Number(it.commissionAmount || 0) || 0), 0);
    return { items: safeItems, total };
  }
}
